// utils/pdfConversion.js - IMPROVED VERSION
import { pdfjs, Document, Page } from 'react-pdf';
import { useState, useEffect, useMemo } from 'react';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';

// Configure the worker for react-pdf with fallback options
if (typeof window !== 'undefined') {
  // Try different worker configurations
  try {
    // First try the module approach
    pdfjs.GlobalWorkerOptions.workerSrc = new URL(
      'pdfjs-dist/build/pdf.worker.min.mjs',
      import.meta.url,
    ).toString();
  } catch (error) {
    // Fallback to CDN
    console.warn('Failed to load local PDF worker, using CDN fallback');
    pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;
  }
}

/**
 * Extracts text from a PDF file using react-pdf with improved error handling
 * @param {File} file - The PDF file to extract text from
 * @returns {Promise<string>} - A promise that resolves to the extracted text
 */
export const extractTextFromPDF = async (file) => {
  try {
    console.log("Starting PDF extraction for file:", file.name);
    
    // Convert the file to an ArrayBuffer
    console.log("Converting file to ArrayBuffer...");
    const arrayBuffer = await file.arrayBuffer();
    console.log("File converted to ArrayBuffer, size:", arrayBuffer.byteLength);
    
    if (arrayBuffer.byteLength === 0) {
      throw new Error("PDF file appears to be empty");
    }
    
    // Convert ArrayBuffer to Uint8Array for pdfjs
    const uint8Array = new Uint8Array(arrayBuffer);
    
    // Load the PDF document with better error handling
    console.log("Loading PDF document with PDF.js...");
    const loadingTask = pdfjs.getDocument({
      data: uint8Array,
      verbosity: 0, // Reduce console noise
      standardFontDataUrl: `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/standard_fonts/`,
      cMapUrl: `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/cmaps/`,
      cMapPacked: true,
    });
    
    // Add timeout and error handling
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(() => reject(new Error('PDF loading timeout after 30 seconds')), 30000);
    });
    
    const pdf = await Promise.race([loadingTask.promise, timeoutPromise]);
    console.log("PDF loaded successfully, page count:", pdf.numPages);
    
    if (pdf.numPages === 0) {
      throw new Error("PDF contains no pages");
    }
    
    let fullText = "";
    const maxPages = Math.min(pdf.numPages, 50); // Limit to 50 pages for performance
    
    // Extract text from each page with better error handling
    console.log(`Processing ${maxPages} pages...`);
    for (let i = 1; i <= maxPages; i++) {
      console.log(`Processing page ${i}/${maxPages}...`);
      try {
        const page = await pdf.getPage(i);
        
        // Get text content with options
        const textContent = await page.getTextContent({
          normalizeWhitespace: true,
          disableCombineTextItems: false,
        });
        
        // Process text items more carefully
        const pageText = textContent.items
          .filter(item => item.str && item.str.trim()) // Filter out empty strings
          .map(item => {
            // Clean up the text
            let text = item.str.trim();
            // Handle spacing between words
            if (item.hasEOL) {
              text += '\n';
            }
            return text;
          })
          .join(' ')
          .replace(/\s+/g, ' ') // Replace multiple spaces with single space
          .trim();
        
        if (pageText && pageText.length > 0) {
          console.log(`Page ${i} processed, extracted ${pageText.length} characters`);
          console.log(`Page ${i} preview:`, pageText.substring(0, 100));
          fullText += pageText + "\n\n"; // Add double newline between pages
        } else {
          console.warn(`Page ${i} contains no extractable text`);
        }
        
        // Clean up page resources
        page.cleanup();
        
      } catch (pageError) {
        console.error(`Error processing page ${i}:`, pageError);
        // Continue with other pages instead of failing completely
        fullText += `[Error extracting text from page ${i}: ${pageError.message}]\n\n`;
      }
    }
    
    // Clean up PDF resources
    pdf.destroy();
    
    const finalText = fullText.trim();
    console.log("PDF extraction completed successfully");
    console.log("Total text length:", finalText.length);
    console.log("Text preview:", finalText.substring(0, 500));
    
    if (!finalText || finalText.length < 50) {
      throw new Error("PDF appears to contain no readable text. It may be an image-based PDF or contain only graphics. Please try uploading a text-based PDF or convert to DOCX format.");
    }
    
    return finalText;
    
  } catch (error) {
    console.error("Error extracting text from PDF:", error);
    
    // Provide more specific error messages
    if (error.message.includes('Worker')) {
      throw new Error("PDF processing failed due to worker issues. Please try refreshing the page and uploading again, or convert your file to DOCX format.");
    } else if (error.message.includes('password')) {
      throw new Error("This PDF is password protected. Please upload an unprotected version.");
    } else if (error.message.includes('Invalid PDF')) {
      throw new Error("This file appears to be corrupted or is not a valid PDF. Please try uploading a different file.");
    } else if (error.message.includes('timeout')) {
      throw new Error("PDF processing timed out. The file may be too large or complex. Please try a smaller file or convert to DOCX format.");
    } else {
      throw new Error(`Failed to extract text from PDF: ${error.message}`);
    }
  }
};

/**
 * Extracts text from a DOCX file using mammoth.js
 * @param {File} file - The DOCX file to extract text from
 * @param {Object} mammoth - The mammoth.js library
 * @returns {Promise<string>} - A promise that resolves to the extracted text
 */
export const extractTextFromDOCX = async (file, mammoth) => {
  try {
    console.log("Starting DOCX extraction for file:", file.name);
    
    if (!mammoth) {
      throw new Error("Mammoth.js library is required for DOCX extraction but was not provided");
    }
    
    // Convert the file to an ArrayBuffer
    const arrayBuffer = await file.arrayBuffer();
    
    if (arrayBuffer.byteLength === 0) {
      throw new Error("DOCX file appears to be empty");
    }
    
    console.log("DOCX file size:", arrayBuffer.byteLength);
    
    // Use mammoth to extract the text
    const result = await mammoth.extractRawText({ arrayBuffer });
    
    const cleanedText = result.value
      .replace(/\r\n/g, '\n') // Normalize line endings
      .replace(/\n\s*\n/g, '\n\n') // Clean up multiple newlines
      .trim();
    
    console.log("DOCX extraction completed, text length:", cleanedText.length);
    console.log("DOCX text preview:", cleanedText.substring(0, 300));
    
    if (!cleanedText || cleanedText.length < 20) {
      throw new Error("DOCX file appears to contain no readable text.");
    }
    
    return cleanedText;
    
  } catch (error) {
    console.error("Error extracting text from DOCX:", error);
    throw new Error(`Failed to extract text from DOCX: ${error.message}`);
  }
};

/**
 * Main function to extract text from document files (PDF, DOCX, TXT)
 * @param {File} file - The file to extract text from
 * @param {Object} dependencies - Additional dependencies like mammoth
 * @returns {Promise<string>} - A promise that resolves to the extracted text
 */
export const extractTextFromFile = async (file, dependencies = {}) => {
  const { mammoth } = dependencies;
  
  console.log("Starting text extraction for file:", {
    name: file.name,
    type: file.type,
    size: file.size
  });
  
  try {
    // Validate file first
    const validation = validateDocumentFile(file);
    if (!validation.valid) {
      throw new Error(validation.message);
    }
    
    // Extract based on file type
    if (file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')) {
      return await extractTextFromPDF(file);
    } else if (file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' || file.name.toLowerCase().endsWith('.docx')) {
      if (!mammoth) {
        throw new Error("Mammoth.js is required for DOCX extraction but was not provided");
      }
      return await extractTextFromDOCX(file, mammoth);
    } else if (file.type === 'text/plain' || file.name.toLowerCase().endsWith('.txt')) {
      const text = await file.text();
      if (!text || text.trim().length < 20) {
        throw new Error("Text file appears to be empty or too short.");
      }
      return text.trim();
    } else if (file.type === 'application/msword' || file.name.toLowerCase().endsWith('.doc')) {
      throw new Error("DOC files are not supported directly. Please save as DOCX or PDF.");
    } else {
      throw new Error(`Unsupported file type: ${file.type}. Please upload a PDF, DOCX, or TXT file.`);
    }
  } catch (error) {
    console.error("Text extraction failed:", error);
    throw error;
  }
};

/**
 * Component to render a PDF preview with memoized options
 * @param {Object} props - Component props
 * @param {File} props.file - The PDF file to preview
 * @param {number} props.maxPages - Maximum number of pages to render
 * @returns {JSX.Element} - The PDF preview component
 */
export const PDFPreview = ({ file, maxPages = 3 }) => {
  const [numPages, setNumPages] = useState(null);
  const [fileURL, setFileURL] = useState(null);
  const [error, setError] = useState(null);
  
  // Memoize options to prevent unnecessary reloads
  const options = useMemo(() => ({
    cMapUrl: `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/cmaps/`,
    cMapPacked: true,
    standardFontDataUrl: `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/standard_fonts/`,
    verbosity: 0,
  }), []);
  
  useEffect(() => {
    if (file) {
      const url = URL.createObjectURL(file);
      setFileURL(url);
      setError(null);
      
      // Clean up the URL when component unmounts
      return () => URL.revokeObjectURL(url);
    }
  }, [file]);
  
  function onDocumentLoadSuccess({ numPages }) {
    setNumPages(numPages);
    setError(null);
  }
  
  function onDocumentLoadError(error) {
    console.error('PDF preview load error:', error);
    setError('Failed to load PDF preview');
  }
  
  if (error) {
    return (
      <div className="pdf-preview-error text-red-600 p-4 border border-red-200 rounded">
        <p>{error}</p>
      </div>
    );
  }
  
  if (!fileURL) {
    return <div>Loading PDF preview...</div>;
  }
  
  return (
    <div className="pdf-preview">
      <Document
        file={fileURL}
        onLoadSuccess={onDocumentLoadSuccess}
        onLoadError={onDocumentLoadError}
        options={options}
        loading={<div>Loading PDF...</div>}
        error={<div className="text-red-600">Error loading PDF preview</div>}
      >
        {Array.from(new Array(Math.min(numPages || 0, maxPages)), (el, index) => (
          <Page 
            key={`page_${index + 1}`} 
            pageNumber={index + 1} 
            width={300}
            renderTextLayer={false} // Disable text layer for preview to reduce errors
            renderAnnotationLayer={false}
            loading={<div>Loading page...</div>}
            error={<div className="text-red-600">Error loading page</div>}
          />
        ))}
      </Document>
      {numPages > maxPages && (
        <p className="text-sm text-gray-500 mt-2">
          Showing {maxPages} of {numPages} pages
        </p>
      )}
    </div>
  );
};

/**
 * Validates document files (PDF, DOC, DOCX, TXT)
 * @param {File} file - The file to validate
 * @param {Object} options - Options like maxSize in bytes
 * @returns {Object} - An object with 'valid' and 'message' properties
 */
export const validateDocumentFile = (file, { maxSize = 10 * 1024 * 1024 } = {}) => {
  // Check if file exists
  if (!file) {
    return { 
      valid: false, 
      message: "No file provided" 
    };
  }
  
  // Check file type
  const validTypes = [
    'application/pdf', 
    'application/msword', 
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/plain'
  ];
  
  const validExtensions = ['.pdf', '.doc', '.docx', '.txt'];
  const fileExtension = file.name.toLowerCase().substr(file.name.lastIndexOf('.'));
  
  if (!validTypes.includes(file.type) && !validExtensions.includes(fileExtension)) {
    return { 
      valid: false, 
      message: "Please upload a PDF, DOC, DOCX, or TXT file" 
    };
  }
  
  // Check file size
  if (file.size > maxSize) {
    return { 
      valid: false, 
      message: `File size must be less than ${Math.round(maxSize / (1024 * 1024))} MB` 
    };
  }
  
  // Check if file is not empty
  if (file.size === 0) {
    return { 
      valid: false, 
      message: "File appears to be empty" 
    };
  }
  
  return { valid: true };
};